import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { COUNTRIES, CountryData, getFlagImageUrl } from '../../data/countries';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** Preferred country codes for shared dial codes (e.g. +1 → US) */
const PREFERRED_FOR_DIAL: Record<string, string> = { '+1': 'US', '+7': 'RU' };

function formatUSPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatCOPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

function detectCountry(value: string, forcedCode?: string): CountryData | null {
  if (!value || !value.startsWith('+')) return null;
  const cleaned = value.replace(/[\s\-()]/g, '');
  const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

  let firstMatch: CountryData | null = null;
  for (const c of sorted) {
    if (cleaned.startsWith(c.dialCode)) {
      if (forcedCode && c.code === forcedCode) return c;
      if (!forcedCode && PREFERRED_FOR_DIAL[c.dialCode] === c.code) return c;
      if (!firstMatch) firstMatch = c;
    }
  }
  return firstMatch;
}

/** Small flag image component */
const FlagImg: React.FC<{ code: string; size?: number; className?: string }> = ({ code, size = 20, className = '' }) => (
  <img
    src={getFlagImageUrl(code)}
    alt={code}
    width={size}
    height={Math.round(size * 0.75)}
    className={`phone-flag-img ${className}`}
    style={{ objectFit: 'cover', borderRadius: 2, display: 'block' }}
    loading="lazy"
  />
);

export const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, placeholder = '+1 (555) 123-4567', className = '' }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 280 });
  const [forcedCountryCode, setForcedCountryCode] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentCountry = useMemo(() => detectCountry(value, forcedCountryCode), [value, forcedCountryCode]);

  const updateDropdownPos = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dropdownHeight = 260;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    if (spaceAbove >= dropdownHeight || spaceAbove > spaceBelow) {
      setDropdownPos({ top: rect.top - Math.min(dropdownHeight, spaceAbove), left: rect.left, width: Math.max(280, rect.width) });
    } else {
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(280, rect.width) });
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on OUTSIDE scroll only (not scroll inside dropdown), and on resize
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown itself
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      setDropdownOpen(false);
    };
    const handleResize = () => setDropdownOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen) updateDropdownPos();
  }, [dropdownOpen, updateDropdownPos]);

  const filteredCountries = useMemo(() => {
    if (!filter.trim()) return COUNTRIES;
    const term = filter.toLowerCase();
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.dialCode.includes(term) ||
      c.code.toLowerCase().includes(term)
    );
  }, [filter]);

  const selectCountry = useCallback((country: CountryData) => {
    const currentDialCode = currentCountry?.dialCode;
    let nationalPart = '';
    if (currentDialCode && value.startsWith(currentDialCode)) {
      nationalPart = value.slice(currentDialCode.length).replace(/[\s\-()]/g, '');
    }
    setForcedCountryCode(country.code);
    onChange(country.dialCode + ' ' + nationalPart);
    setDropdownOpen(false);
    setFilter('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [currentCountry, value, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    if (raw.length < value.length) { onChange(raw); return; }
    if (raw && !raw.startsWith('+') && /^\d/.test(raw)) raw = '+' + raw;

    const detected = detectCountry(raw, forcedCountryCode);
    if (detected) {
      const nationalDigits = raw.slice(detected.dialCode.length).replace(/\D/g, '');
      if (['US', 'CA', 'PR', 'JM', 'DO'].includes(detected.code)) {
        onChange(`${detected.dialCode} ${formatUSPhone(nationalDigits)}`);
        return;
      }
      if (detected.code === 'CO') {
        onChange(`${detected.dialCode} ${formatCOPhone(nationalDigits)}`);
        return;
      }
    }
    onChange(raw);
  }, [value, onChange, forcedCountryCode]);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen(prev => !prev);
    setFilter('');
  }, []);

  const dropdownPortal = dropdownOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="phone-dropdown"
      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
    >
      <input
        type="text"
        className="phone-dropdown-filter"
        placeholder="Search country..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
        autoFocus
      />
      <div className="phone-dropdown-list">
        {filteredCountries.map(c => (
          <div
            key={c.code}
            className={`phone-dropdown-item${currentCountry?.code === c.code ? ' active' : ''}`}
            onMouseDown={() => selectCountry(c)}
          >
            <FlagImg code={c.code} size={18} />
            <span className="phone-dropdown-name">{c.name}</span>
            <span className="phone-dropdown-dial">{c.dialCode}</span>
          </div>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="phone-input-wrapper" ref={ref}>
      <button
        type="button"
        className="phone-flag-btn"
        onClick={toggleDropdown}
        title={currentCountry ? `${currentCountry.name} (${currentCountry.dialCode})` : 'Select country code'}
        tabIndex={-1}
      >
        {currentCountry ? <FlagImg code={currentCountry.code} size={18} /> : <span className="phone-flag-code">--</span>}
        <span className="phone-flag-caret">▾</span>
      </button>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={`phone-input has-flag-btn ${className}`}
      />
      {dropdownPortal}
    </div>
  );
};
